import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import styles from "./Select.module.scss";

type Props = {
	children?: React.ReactNode;
	label: string;
	placeholder: string;
	items: SelectItem[];
	selected?: SelectItem;
	required?: boolean;
	searchable?: boolean;
	error?: string;
	onChange?: (item: SelectItem | undefined) => void;
};

export class SelectItem {
	key: any;
	label: string;

	constructor(key: any, label: string) {
		this.key = key;
		this.label = label;
	}
}

const Select = memo((props: Props) => {
	const { children, label, onChange, searchable, placeholder, items, selected, required } = props;

	const [showOnTop, setShowOnTop] = useState(false);

	const [dropdownExpanded, setDropdownExpanded] = useState(false);
	const [selectedItem, setSelectedItem] = useState<SelectItem | undefined>(selected);

	const [search, setSearch] = useState<string>("");
	const [value, setValue] = useState<string | null>(null);
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const inputRef = useRef<HTMLInputElement>(null);
	const itemsRef = useRef<HTMLDivElement>(null);

	const filteredItems = useMemo(() => {
		return items.filter(x => x.label.toLocaleLowerCase().search(search.toLocaleLowerCase()) !== -1);
	}, [items, search]);

	const selectItem = useCallback(
		(item: SelectItem, event: React.MouseEvent) => {
			setSelectedItem(item);
			setSearch("");
			setValue(item.label);
			setDropdownExpanded(false);
			if (onChange) {
				onChange(item);
			}
		},
		[onChange, setSelectedItem, setSearch, setDropdownExpanded]
	);

	function changeDropdownState(): void {
		setDropdownExpanded(!dropdownExpanded);
	}

	useEffect(() => {
		if (selected != null) setValue(selected.label);
	}, []);

	const close = useCallback(() => {
		setDropdownExpanded(false);
		setSearch("");
		if (!items.some(item => item.label === value)) {
			setValue("");
			if (onChange) {
				onChange(undefined);
			}
		}
	}, [items, value, onChange]);

	function getLabel(): string {
		if (selectedItem) {
			return selectedItem.label;
		}
		return "";
	}

	function handleMouseDown(event: React.MouseEvent): void {
		event.preventDefault();
	}

	function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
		setSearch(event.target.value);
		setValue(event.target.value);
		setDropdownExpanded(true);
	}

	const registerKeyEventsForInput = useCallback(() => {
		let inputRefCopy = inputRef;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				if (highlightedIndex === filteredItems.length - 1) return;
				setHighlightedIndex(prevHighlightedIndex => prevHighlightedIndex + 1);
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				if (highlightedIndex === 0) return;
				setHighlightedIndex(prevHighlightedIndex => prevHighlightedIndex - 1);
			} else if (event.key === "Enter") {
				event.preventDefault();
				selectItem(filteredItems[highlightedIndex], event as any);
			} else if (event.key === "Escape") {
				event.preventDefault();
				close();
			}
		};

		inputRefCopy.current?.addEventListener("keydown", handleKeyDown);

		return () => {
			inputRefCopy?.current?.removeEventListener("keydown", handleKeyDown);
		};
	}, [highlightedIndex, filteredItems, inputRef, selectItem, close]);

	useEffect(registerKeyEventsForInput, [registerKeyEventsForInput]);

	useEffect(() => {
		setSelectedItem(selected);
	}, [selected]);

	useEffect(() => {
		setHighlightedIndex(filteredItems.findIndex(x => x.key === selected?.key));
	}, [selected, filteredItems]);

	useEffect(() => {
		if (highlightedIndex < 3) {
			itemsRef.current?.scrollTo({ top: 0, behavior: "smooth" });
			return;
		}
		itemsRef.current?.children[highlightedIndex]?.scrollIntoView({ behavior: "smooth", block: "center" });
	});

	useEffect(() => {
		if (!dropdownExpanded) return;

		const dropdown = document.getElementById("select-button");
		if (!dropdown) return;

		const dropdownRect = dropdown.getBoundingClientRect();
		const bottomOverflow = window.innerHeight - (dropdownRect.bottom + window.innerHeight * 0.225);
		const topOverflow = dropdownRect.top - window.innerHeight * 0.225;

		if (bottomOverflow < 0) {
			setShowOnTop(true);
		} else if (topOverflow < 0) {
			setShowOnTop(false);
		} else {
			setShowOnTop(false);
		}
	}, [dropdownExpanded]);

	const handleContainerClick = () => {
		if (inputRef?.current) {
			inputRef.current.focus();
			changeDropdownState();
		}
	};

	return (
		<button className={styles.container} onClick={handleContainerClick}>
			{children && <div className={`${styles.inputIcon} ${styles.orangeBg}`}>{children}</div>}
			<div className={styles.inputContent}>
				<span className={styles.inputLabel}>
					{label} {required && <div className={styles.asterix}>&nbsp;*</div>}
				</span>
				<div role="none" className={styles.selectContainer} onBlur={close}>
					<div role="none" className={styles.selectButton} id="select-button" onClick={changeDropdownState} onBlur={close}>
						{searchable ? (
							<input type="text" ref={inputRef} className={styles.searchInput} placeholder={placeholder} onChange={changeSearch} value={value ?? search} />
						) : (
							<input readOnly ref={inputRef} className={`${styles.searchInput} ${styles.placeholderInput}`} placeholder={placeholder} value={getLabel()} />
						)}
					</div>
					{props.error && <div className={styles.error}>{props.error}</div>}
					{
						<div
							id="select-dropdown"
							className={`${styles.selectExpanded} ${!dropdownExpanded ? styles.selectClosed : ""} ${showOnTop ? styles.selectExpandedTop : ""}`}
							role="none"
							onMouseDown={handleMouseDown}
						>
							<div className={styles.selectItems} ref={itemsRef}>
								{(() => {
									if (filteredItems.length === 0) {
										return <div className={styles.selectItem}>No items found</div>;
									}

									return filteredItems.map((item, index) => (
										<span
											key={item.key}
											className={`${styles.selectItem} ${index === highlightedIndex ? styles.selectItemHovered : ""}`}
											onClick={event => selectItem(item, event)}
										>
											{item.label}
										</span>
									));
								})()}
							</div>
						</div>
					}
				</div>
			</div>
			<span className={`standardButton ${styles.selectBtn}`} onClick={changeDropdownState}>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M4.23447 5.83438C4.3845 5.6844 4.58794 5.60015 4.80007 5.60015C5.0122 5.60015 5.21565 5.6844 5.36567 5.83438L8.00007 8.46878L10.6345 5.83438C10.7083 5.75797 10.7965 5.69703 10.8941 5.6551C10.9918 5.61317 11.0967 5.5911 11.203 5.59018C11.3092 5.58926 11.4145 5.6095 11.5128 5.64972C11.6112 5.68995 11.7005 5.74935 11.7756 5.82446C11.8507 5.89958 11.9101 5.9889 11.9503 6.08722C11.9906 6.18553 12.0108 6.29088 12.0099 6.3971C12.009 6.50332 11.9869 6.6083 11.945 6.7059C11.903 6.80351 11.8421 6.89178 11.7657 6.96558L8.56567 10.1656C8.41565 10.3156 8.2122 10.3998 8.00007 10.3998C7.78794 10.3998 7.5845 10.3156 7.43447 10.1656L4.23447 6.96558C4.0845 6.81556 4.00024 6.61211 4.00024 6.39998C4.00024 6.18785 4.0845 5.9844 4.23447 5.83438Z"
						fill="#FF7926"
					/>
				</svg>
			</span>
		</button>
	);
});

export default Select;
